import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase';
import { createClient as createMainAppClient } from '@supabase/supabase-js';
import type { OrganizationStatus } from '@/types/database';

export interface OrganizationWithProgress {
  id: string;
  name: string;
  status: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;

  // Calculated fields
  locationCount: number;
  itTasksPending: number;
  itTasksCompleted: number;
  dataTasksPending: number;
  dataTasksCompleted: number;
  platformTasksPending: number;
  platformTasksCompleted: number;

  // Derived stage
  currentStage: string;
  progressPercentage: number;
  lastActivityAt: string | null;
  lastActivityBy: string | null;
}

export interface OrganizationFilterOptions {
  status?: OrganizationStatus;
  itStatus?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED';
  dataStatus?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED';
  searchQuery?: string;
  limit?: number;
  offset?: number;
}

export function useOrganizationPipeline(options?: OrganizationFilterOptions) {
  const [organizations, setOrganizations] = useState<OrganizationWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const supabase = createClient();

      // Fetch all organizations with location counts
      let query = supabase
        .from('organizations')
        .select(
          `
          id,
          name,
          status,
          logo_url,
          created_at,
          updated_at,
          locations:locations(id, status)
        `,
          { count: 'exact' }
        );

      // Apply status filter
      if (options?.status) {
        query = query.eq('status', options.status);
      }

      // Apply pagination
      const limit = options?.limit || 50;
      const offset = options?.offset || 0;

      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data: orgData, error: orgError, count } = await query;

      if (orgError) throw orgError;

      // Fetch ALL task counts in a single query using GROUP BY
      // This replaces the N+1 queries with a single efficient aggregation
      const { data: taskCounts, error: taskError } = await supabase
        .from('tasks')
        .select(
          `
          organization_id,
          type,
          status,
          id
        `
        );

      if (taskError) throw taskError;

      // Process task counts by organization
      const taskCountMap = new Map<
        string,
        {
          itPending: number;
          itCompleted: number;
          dataPending: number;
          dataCompleted: number;
          platformPending: number;
          platformCompleted: number;
        }
      >();

      // Initialize counts for all orgs
      if (orgData) {
        orgData.forEach((org) => {
          taskCountMap.set(org.id, {
            itPending: 0,
            itCompleted: 0,
            dataPending: 0,
            dataCompleted: 0,
            platformPending: 0,
            platformCompleted: 0,
          });
        });
      }

      // Count tasks by type and status
      taskCounts?.forEach((task: any) => {
        const counts = taskCountMap.get(task.organization_id);
        if (!counts) return;

        const isIT = ['IT_PROVISIONING', 'IT_DECONFIG'].includes(task.type);
        const isData = ['DATA_CONFIGURATION', 'DATA_DECONFIG'].includes(task.type);
        const isPlatform = ['MANUAL_REVIEW', 'PAYMENT', 'FINAL_ACTIVATION', 'DEACTIVATION_REVIEW'].includes(
          task.type
        );

        if (isIT && task.status === 'PENDING') counts.itPending++;
        else if (isIT && task.status === 'COMPLETED') counts.itCompleted++;
        else if (isData && task.status === 'PENDING') counts.dataPending++;
        else if (isData && task.status === 'COMPLETED') counts.dataCompleted++;
        else if (isPlatform && task.status === 'PENDING') counts.platformPending++;
        else if (isPlatform && task.status === 'COMPLETED') counts.platformCompleted++;
      });

      // Cross-reference with main app: fetch ALL active location IDs (not deleted, not archived).
      // Any ops portal location NOT in this set is either hard-deleted or archived in the main app.
      // We use an "active set" approach because user-initiated deactivation hard-deletes the row
      // entirely — there's nothing left to query by deleted_at.
      const activeMainAppLocationIds = new Set<string>();
      const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
      const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;
      let mainAppLoaded = false;

      if (mainAppUrl && serviceRoleKey) {
        try {
          const mainApp = createMainAppClient(mainAppUrl, serviceRoleKey, {
            auth: { autoRefreshToken: false, persistSession: false },
          });

          const { data: activeLocs } = await mainApp
            .from('locations')
            .select('id')
            .is('archived_at', null);

          if (activeLocs) {
            mainAppLoaded = true;
            for (const loc of activeLocs) {
              activeMainAppLocationIds.add(loc.id);
            }
          }
        } catch (err) {
          console.error('Failed to fetch active locations from main app:', err);
        }
      }

      // Build organizations with progress
      const orgsWithProgress: OrganizationWithProgress[] = [];

      if (orgData) {
        orgData.forEach((org) => {
          const counts = taskCountMap.get(org.id) || {
            itPending: 0,
            itCompleted: 0,
            dataPending: 0,
            dataCompleted: 0,
            platformPending: 0,
            platformCompleted: 0,
          };

          const totalTasks =
            counts.itPending +
            counts.itCompleted +
            counts.dataPending +
            counts.dataCompleted +
            counts.platformPending +
            counts.platformCompleted;

          const completedTasks = counts.itCompleted + counts.dataCompleted + counts.platformCompleted;

          const progressPercentage = totalTasks > 0
            ? Math.round((completedTasks / totalTasks) * 100)
            : 0;

          orgsWithProgress.push({
            id: org.id,
            name: org.name,
            status: org.status,
            logo_url: org.logo_url,
            created_at: org.created_at,
            updated_at: org.updated_at,
            locationCount: org.locations?.filter((loc: any) =>
              loc.status !== 'DELETING' &&
              loc.status !== 'DEACTIVATED' &&
              (!mainAppLoaded || activeMainAppLocationIds.has(loc.id))
            ).length || 0,
            itTasksPending: counts.itPending,
            itTasksCompleted: counts.itCompleted,
            dataTasksPending: counts.dataPending,
            dataTasksCompleted: counts.dataCompleted,
            platformTasksPending: counts.platformPending,
            platformTasksCompleted: counts.platformCompleted,
            currentStage: org.status,
            progressPercentage,
            lastActivityAt: org.updated_at,
            lastActivityBy: null,
          });
        });
      }

      setOrganizations(orgsWithProgress);
      setTotal(count || 0);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch organizations'));
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [options?.status, options?.limit, options?.offset]);

  return { organizations, loading, error, total, refetch: fetchOrganizations };
}
